<template>
    <!-- Role sheet (seats model): one seat at a time, every seat stacked, or all seats side by side -->
    <div class="rv" @click="onContentClick">
        <div class="rv-top">
            <div class="rv-count">{{ seats.length }} seat{{ seats.length == 1 ? '' : 's' }} · {{ sections.length }} section{{ sections.length == 1 ? '' : 's' }} each</div>
            <div v-if="seats.length > 1" class="ep-seg rv-tog" role="tablist" aria-label="How to show the seats">
                <button v-for="m in modes" :key="m.id" type="button" role="tab" :aria-selected="mode == m.id" :class="{ on: mode == m.id }" @click="mode = m.id">
                    <svg viewBox="0 0 24 24"><path :d="m.icon" /></svg>{{ m.label }}
                </button>
            </div>
        </div>

        <div v-if="!seats.length" class="rv-empty">No seats on this role sheet yet.</div>

        <!-- One seat -->
        <template v-else-if="mode == 'seat'">
            <div v-if="seats.length > 1" class="rv-seats" role="tablist" aria-label="Seats">
                <button v-for="(seat, i) in seats" :key="seat.id" type="button" role="tab" :aria-selected="selected == i" :class="['rv-seat', { on: selected == i }]" @click="selected = i">
                    <span class="rs-seat-i">{{ initials(seat) }}</span>
                    <span class="rv-seat-t"><b>{{ heading(seat).main || 'Seat ' + (i + 1) }}</b><em v-if="heading(seat).sub">{{ heading(seat).sub }}</em></span>
                </button>
            </div>
            <div class="rv-page">
                <header class="rv-seathead">
                    <span class="rv-big">{{ initials(current) }}</span>
                    <div class="rv-seathead-t">
                        <div class="rv-sn">{{ heading(current).main || 'Seat ' + (selected + 1) }}</div>
                        <div v-if="heading(current).sub" class="rv-st">{{ heading(current).sub }}</div>
                        <div v-if="reportsTo(current)" class="rv-chips"><span class="rv-chip"><svg viewBox="0 0 24 24"><path d="M12 19V5M6 11l6-6 6 6" /></svg>Reports to {{ reportsTo(current) }}</span></div>
                    </div>
                    <div v-if="seats.length > 1" class="rv-nav">
                        <button type="button" aria-label="Previous seat" @click="selected = (selected - 1 + seats.length) % seats.length"><svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg></button>
                        <span>{{ selected + 1 }} of {{ seats.length }}</span>
                        <button type="button" aria-label="Next seat" @click="selected = (selected + 1) % seats.length"><svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg></button>
                    </div>
                </header>
                <div v-if="actions" class="rv-actions">
                    <button type="button" class="btn rv-act" @click="$emit('sign', current)"><svg viewBox="0 0 24 24"><path d="M3 17c3.33-3.33 5-6 5-8a3 3 0 0 0-6 0c0 3 3 4.5 4.5 6.5C7.5 16.8 8.3 18 9 18c1.5 0 2.5-3 3-3s1 2 2.5 2c.6 0 1.6-.6 2.5-2M17 20h4" /></svg>Sign off</button>
                    <button type="button" class="ep-chip" @click="$emit('share', current)"><svg viewBox="0 0 24 24"><circle cx="6" cy="12" r="3" /><circle cx="18" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><path d="M8.7 10.7l6.6-3.4M8.7 13.3l6.6 3.4" /></svg>Share</button>
                    <button type="button" class="ep-chip" @click="copySeat(current)"><svg viewBox="0 0 24 24"><path d="M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2V9zM5 15H4a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2h9a1 1 0 0 1 1 1v1" /></svg>{{ copied == 'text' ? 'Copied' : 'Copy' }}</button>
                    <button type="button" class="ep-chip" @click="copyLink(current)"><svg viewBox="0 0 24 24"><path d="M9 15l6-6M11 6l.46-.54a5 5 0 0 1 7.07 7.07L18 13M13 18l-.4.53a5.07 5.07 0 0 1-7.12 0 4.97 4.97 0 0 1 0-7.07L6 11" /></svg>{{ copied == 'link' ? 'Link copied' : 'Copy link' }}</button>
                </div>
                <!-- not <nav>: the global nav styles are for the side menu -->
                <div v-if="filled(current).length > 2" class="rv-toc" role="navigation" aria-label="Sections">
                    <a v-if="accs(current).length" @click.prevent="jump('acc')">Accountabilities</a>
                    <a v-for="section in filled(current)" :key="section.id" @click.prevent="jump(section.id)">{{ section.label.replace(/\s*\(\d+\)\s*$/, '') }}</a>
                </div>
                <role-seat :accountabilities="current.accountabilities" :sections="sectionsOf(current)" :prefix="'rv-' + current.id" />
                <p v-if="!accs(current).length && !filled(current).length" class="rv-empty">This seat hasn't been filled in yet.</p>
                <p v-if="isAdmin && missing(current)" class="rv-missing">
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
                    {{ missing(current) }} section{{ missing(current) == 1 ? " isn't" : "s aren't" }} filled in for this seat, so {{ missing(current) == 1 ? "it's" : "they're" }} hidden. Edit the role sheet to add {{ missing(current) == 1 ? 'it' : 'them' }}.
                </p>
            </div>
        </template>

        <!-- View all: every seat, one after another -->
        <template v-else-if="mode == 'all'">
            <div class="rv-toc rv-alljump" role="navigation" aria-label="Seats">
                <a v-for="(seat, i) in seats" :key="seat.id" @click.prevent="jumpSeat(i)"><span class="rs-seat-i">{{ initials(seat) }}</span>{{ heading(seat).main || 'Seat ' + (i + 1) }}</a>
            </div>
            <div v-for="(seat, i) in seats" :key="seat.id" :id="'rv-seat-' + i" class="rv-page rv-allpage">
                <header class="rv-seathead">
                    <span class="rv-big">{{ initials(seat) }}</span>
                    <div class="rv-seathead-t">
                        <div class="rv-sn">{{ heading(seat).main || 'Seat ' + (i + 1) }}</div>
                        <div v-if="heading(seat).sub" class="rv-st">{{ heading(seat).sub }}</div>
                    </div>
                    <div class="rv-nav"><span>Seat {{ i + 1 }} of {{ seats.length }}</span></div>
                </header>
                <role-seat :accountabilities="seat.accountabilities" :sections="sectionsOf(seat)" :prefix="'rv-all-' + seat.id" />
                <p v-if="!accs(seat).length && !filled(seat).length" class="rv-empty">This seat hasn't been filled in yet.</p>
            </div>
        </template>

        <!-- Side by side: one column per seat, a band per section -->
        <template v-else>
            <div class="rv-cmp-bar">
                <span>Scroll sideways to see every seat. Click a seat name to read it on its own.</span>
                <div class="rv-zoom" role="group" aria-label="Text size">
                    <button type="button" aria-label="Smaller text" :disabled="zoom <= 0.8" @click="setZoom(-0.1)"><svg viewBox="0 0 24 24"><path d="M5 12h14" /></svg></button>
                    <span>{{ Math.round(zoom * 100) }}%</span>
                    <button type="button" aria-label="Larger text" :disabled="zoom >= 1.3" @click="setZoom(0.1)"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg></button>
                </div>
            </div>
            <div class="rv-cmp-wrap">
                <table class="rv-cmp" :style="{ fontSize: (13.5 * zoom).toFixed(1) + 'px', width: seats.length * colWidth + 'px' }">
                    <colgroup><col v-for="seat in seats" :key="seat.id" :style="{ width: colWidth + 'px' }" /></colgroup>
                    <thead>
                        <tr>
                            <th v-for="(seat, i) in seats" :key="seat.id" class="rv-cmp-seat">
                                <button type="button" title="Open this seat on its own" @click="openSeat(i)">
                                    <span class="rs-seat-i">{{ initials(seat) }}</span>
                                    <span><b>{{ heading(seat).main || 'Seat ' + (i + 1) }}</b><em v-if="heading(seat).sub">{{ heading(seat).sub }}</em></span>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="seats.some(s => accs(s).length)">
                            <tr class="rv-band"><th :colspan="seats.length"><span>Accountabilities</span></th></tr>
                            <tr>
                                <td v-for="seat in seats" :key="seat.id" :class="['rv-cmp-cell', { empty: !accs(seat).length }]">
                                    <ol v-if="accs(seat).length" class="rv-rich rv-accl"><li v-for="(a, k) in accs(seat)" :key="k"><b>{{ a.value }}</b><span v-if="a.value2"> — {{ a.value2 }}</span></li></ol>
                                    <span v-else>Not filled in</span>
                                </td>
                            </tr>
                        </template>
                        <template v-for="section in sections" :key="section.id">
                            <tr class="rv-band"><th :colspan="seats.length"><span>{{ section.label }}</span></th></tr>
                            <tr>
                                <td v-for="seat in seats" :key="seat.id" :class="['rv-cmp-cell', { empty: !hasText(seat.values[section.id]) }]">
                                    <div v-if="hasText(seat.values[section.id])" class="rv-rich ve-body" v-html="seat.values[section.id]"></div>
                                    <span v-else>Not filled in</span>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import RoleSeat from '@/components/RoleSeat.vue';
import { seatInitials, seatNameTitle, seatLabel, plainText, labelKey, seatSections } from '@/roleSheet';

function hasText(html: any): boolean {
    return !!plainText(html);
}

export default defineComponent({
    name: 'RoleSheetView',
    components: { RoleSeat },
    // sheet: the seats model. initialSeatId: the seat to open first (?col= link, or the viewer's own seat).
    // actions: show Sign off / Share / Copy / Copy link (off in edit-page Preview)
    props: {
        sheet: { type: Object, required: true },
        initialSeatId: { type: String, default: null },
        actions: { type: Boolean, default: true },
        isAdmin: { type: Boolean, default: false }
    },
    emits: [ 'sign', 'share', 'open-link' ],
    data() {
        return {
            mode: 'seat',
            selected: 0,
            zoom: 1,
            copied: '',
            modes: [
                { id: 'seat', label: 'One seat', icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1' },
                { id: 'all', label: 'View all', icon: 'M4 6h16M4 12h16M4 18h10' },
                { id: 'compare', label: 'Side by side', icon: 'M4 4h5v16H4zM10 4h4v16h-4zM15 4h5v16h-5z' }
            ]
        }
    },
    computed: {
        seats(): any[] {
            return (this.sheet as any).seats || [];
        },
        sections(): any[] {
            return (this.sheet as any).sections || [];
        },
        current(): any {
            return this.seats[Math.min(this.selected, this.seats.length - 1)] || null;
        },
        colWidth(): number {
            return Math.round(300 * this.zoom);
        }
    },
    created() {
        if(this.initialSeatId) {
            const i = this.seats.findIndex((s: any) => s.id == this.initialSeatId);
            if(i > -1) this.selected = i;
        }
    },
    methods: {
        hasText,
        initials(seat: any): string {
            return seatInitials(seat);
        },
        // The seat's name in bold with its title under it; just the title when it has no name
        heading(seat: any): { main: string, sub: string } {
            const { name, title } = seatNameTitle(seat);
            return { main: name || title, sub: name ? title : '' };
        },
        accs(seat: any): any[] {
            return (seat.accountabilities || []).filter((a: any) => a && (a.value || a.value2));
        },
        sectionsOf(seat: any): any[] {
            return seatSections(this.sheet, seat);
        },
        filled(seat: any): any[] {
            return this.sections.filter((s: any) => hasText((seat.values || {})[s.id]));
        },
        missing(seat: any): number {
            return this.sections.length - this.filled(seat).length;
        },
        // A short "Reports to" chip when the sheet has a section that's just that
        reportsTo(seat: any): string {
            const section = this.sections.find((s: any) => /^(you )?report(s|ing)? to$/.test(labelKey(s.label)));
            const text = section ? plainText((seat.values || {})[section.id]) : '';
            return text.length > 60 ? text.slice(0, 57) + '…' : text;
        },
        jump(id: string) {
            const el = document.getElementById('rv-' + this.current.id + '-' + id);
            if(el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        jumpSeat(i: number) {
            const el = document.getElementById('rv-seat-' + i);
            if(el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        openSeat(i: number) {
            this.selected = i;
            this.mode = 'seat';
        },
        setZoom(change: number) {
            this.zoom = Math.round(Math.max(0.8, Math.min(1.3, this.zoom + change)) * 10) / 10;
        },
        flash(what: string) {
            this.copied = what;
            setTimeout(() => { if(this.copied == what) this.copied = ''; }, 1600);
        },
        // Copies the seat as formatted text (pastes into email / Word with headings and bullets)
        async copySeat(seat: any) {
            const escape = (t: string) => `${t}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            let html = `<h2>${escape(seatLabel(seat))}</h2>`;
            const accs = this.accs(seat);
            if(accs.length) html += `<h3>Accountabilities (${accs.length})</h3><ol>` + accs.map((a: any) => `<li><b>${escape(a.value || '')}</b>${a.value2 ? ' — ' + escape(a.value2) : ''}</li>`).join('') + '</ol>';
            for(const section of this.filled(seat)) html += `<h3>${escape(section.label || '')}</h3>${seat.values[section.id]}`;
            const box = document.createElement('div');
            box.innerHTML = html;
            const text = box.innerText || plainText(html);
            try {
                await navigator.clipboard.write([ new ClipboardItem({ 'text/html': new Blob([ html ], { type: 'text/html' }), 'text/plain': new Blob([ text ], { type: 'text/plain' }) }) ]);
            } catch(e) {
                await navigator.clipboard.writeText(text).catch(() => null); // clipboard blocked: nothing else to try
            }
            this.flash('text');
        },
        // Link straight to this seat (?col= is the seat id, the same id the old column links used)
        copyLink(seat: any) {
            navigator.clipboard.writeText(window.location.href.split('?')[0] + '?col=' + seat.id).catch(() => null);
            this.flash('link');
        },
        // Links inside section text: Playbook.vue opens /playbook/ links in its popup
        onContentClick(e: any) {
            const a = e.target.closest && e.target.closest('.rv-rich a[href]');
            if(!a) return;
            e.preventDefault();
            this.$emit('open-link', a.getAttribute('href'));
        }
    }
});
</script>
